/**
 * DraftRegistry — a context-based registry for question components that hold
 * uncommitted local drafts (e.g. NumberQuestion's on-blur commit model).
 *
 * Why this exists: NumberQuestion only commits its draft to `answers` on blur.
 * When the RN types `103.4` and immediately taps Complete (no manual tap-out),
 * the field has not yet committed. The validation pass that follows would
 * therefore see stale `answers`, and any branching follow-ups the new value
 * would have triggered (e.g. fever follow-ups for temp > 101) are not yet
 * visible to the validator.
 *
 * Solution: the section that owns the form lifecycle wires up a registry; each
 * draft-holding question registers a `flushIfDirty` function on mount and
 * unregisters on unmount. Before validation, the section iterates the registry
 * and synchronously flushes every dirty draft — no bridge round-trip, no OS
 * keyboard timing dependency. The setState calls from flushing are then yielded
 * for so React can commit them before validation reads the new state.
 */
import { createContext, useContext } from "react";

export interface DraftHolder {
    flushIfDirty: () => void;
}

export interface DraftRegistry {
    register: (id: string, holder: DraftHolder) => void;
    unregister: (id: string) => void;
    flushAll: () => void;
}

const noopRegistry: DraftRegistry = {
    register: () => {},
    unregister: () => {},
    flushAll: () => {}
};

export const DraftRegistryContext = createContext<DraftRegistry>(noopRegistry);

export function useDraftRegistry(): DraftRegistry {
    return useContext(DraftRegistryContext);
}
